import React, { useEffect, useState } from "react";
import DashboardShell from "../../components/DashboardShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, PlayCircle, Save, Loader2 } from "lucide-react";

const defaultDsl = `match: /example/i
action: flag
reason: "example rule"`;

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", dsl_source: defaultDsl, action: "deny", enabled: true, tags: [] });
  const [sim, setSim] = useState({ prompt: "", result: null, loading: false });

  const load = () => api.get("/v1/policies").then(r => setPolicies(r.data));
  useEffect(() => { load(); }, []);

  const select = (p) => {
    setSelected(p);
    setForm({ name: p.name, description: p.description, dsl_source: p.dsl_source, action: p.action, enabled: p.enabled, tags: p.tags || [] });
    setSim({ prompt: "", result: null, loading: false });
  };

  const create = () => {
    setSelected(null);
    setForm({ name: "New Policy", description: "", dsl_source: defaultDsl, action: "deny", enabled: true, tags: [] });
  };

  const save = async () => {
    try {
      if (selected) {
        const { data } = await api.put(`/v1/policies/${selected.id}`, form);
        setSelected(data);
        toast.success("Policy updated · v" + data.version);
      } else {
        const { data } = await api.post(`/v1/policies`, form);
        setSelected(data);
        toast.success("Policy created");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete policy "${p.name}"?`)) return;
    await api.delete(`/v1/policies/${p.id}`);
    if (selected?.id === p.id) setSelected(null);
    load();
  };

  const simulate = async () => {
    if (!selected) { toast.error("Save policy first to simulate."); return; }
    setSim(s => ({ ...s, loading: true }));
    try {
      const { data } = await api.post(`/v1/policies/${selected.id}/simulate`, { prompt: sim.prompt });
      setSim(s => ({ ...s, result: data, loading: false }));
    } catch { setSim(s => ({ ...s, loading: false })); }
  };

  return (
    <DashboardShell
      title="Policies"
      subtitle="Author, version, and simulate governance policies."
      actions={<button data-testid="new-policy" onClick={create} className="btn-primary text-sm"><Plus size={14}/> New policy</button>}
    >
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 eyebrow text-[#FFD98A]" style={{fontSize:10}}>POLICY LIBRARY</div>
          <div className="max-h-[520px] overflow-y-auto scroll-dark">
            {policies.map(p => (
              <button key={p.id} data-testid={`policy-item-${p.id}`} onClick={() => select(p)}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition ${selected?.id===p.id?"bg-white/5":""}`}>
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-sm">{p.name}</div>
                  <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    p.action==="deny"?"bg-red-500/15 text-red-300":
                    p.action==="redact"?"bg-amber-500/15 text-amber-300":
                    p.action==="flag"?"bg-orange-500/15 text-orange-300":"bg-emerald-500/15 text-emerald-300"
                  }`}>{p.action}</span>
                </div>
                <div className="text-xs text-cream/50 mt-1 line-clamp-1">{p.description}</div>
                <div className="text-[10px] text-cream/40 mt-1 font-mono">v{p.version} · {p.tags?.join(", ")}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl bg-white/5 border border-white/10 p-5">
          <div className="flex items-center justify-between">
            <div className="eyebrow text-[#FFD98A]" style={{fontSize:10}}>{selected ? `EDITING · ${selected.name}` : "NEW POLICY"}</div>
            <div className="flex gap-2">
              {selected && <button data-testid="delete-policy" onClick={() => remove(selected)} className="btn-ghost-dark text-xs"><Trash2 size={12}/> Delete</button>}
              <button data-testid="save-policy" onClick={save} className="btn-primary text-xs"><Save size={12}/> {selected ? "Save version" : "Create"}</button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-[11px] text-cream/60">Name</label>
              <input data-testid="policy-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
            </div>
            <div>
              <label className="text-[11px] text-cream/60">Default action</label>
              <select data-testid="policy-action" value={form.action} onChange={e => setForm({...form, action: e.target.value})}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40">
                <option value="allow">allow</option>
                <option value="deny">deny</option>
                <option value="redact">redact</option>
                <option value="flag">flag</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-cream/60">Description</label>
              <input data-testid="policy-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
            </div>
          </div>

          <label className="text-[11px] text-cream/60 mt-4 block">SentryDSL source</label>
          <textarea data-testid="policy-dsl" value={form.dsl_source} onChange={e => setForm({...form, dsl_source: e.target.value})}
            rows={9}
            className="w-full mt-1 bg-black/40 border border-white/10 rounded-lg p-3 font-mono text-sm text-cream outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />

          <div className="mt-6 border-t border-white/10 pt-4">
            <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>SIMULATE</div>
            <div className="flex gap-2">
              <input data-testid="sim-input" placeholder="Enter a test prompt…" value={sim.prompt} onChange={e => setSim(s=>({...s, prompt: e.target.value}))}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
              <button data-testid="sim-run" onClick={simulate} className="btn-primary text-xs">
                {sim.loading ? <Loader2 size={12} className="animate-spin"/> : <PlayCircle size={12}/>} Simulate
              </button>
            </div>
            {sim.result && (
              <div className="mt-3 rounded-lg bg-black/30 border border-white/10 p-3 font-mono text-xs">
                <div>action: <span className="text-[#FF6B35] font-bold">{sim.result.verdict.action}</span></div>
                <div>reason: <span className="text-cream/80">{sim.result.verdict.reason}</span></div>
                {sim.result.verdict.matched && <div>matched: <span className="text-cream/60">{sim.result.verdict.matched}</span></div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
